import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    useOrganizationService,
    useProjectService,
    useRoadmapService,
    useTemplateService
} from "../../../services";
import SelectEntities from "../../../features/SelectEntity/SelectEntities";
import { Badge, Label, Tooltip } from "flowbite-react";
import DatePicker from "react-datepicker";
import { formatDate } from "../../../helpers/data-format";
import toast from 'react-hot-toast';
import { useJwt } from "../../../helpers/common";
import { Drawer } from "../../../components/Modals";
import CustomDropdown from "../../../components/common/CustomDropdown";
import ColorPicker from '../../../components/common/ColorPicker';
import { useDispatch, useSelector } from "react-redux";
import { HiLink, HiQuestionMarkCircle, HiTrash } from "react-icons/hi";
import { setResourcesbyIds } from '../../../redux/slices/resources';
import { getDefaultUserName } from "../../../constants/defaultUsers";
import RichTextEditor from '../../../components/RichTextEditor';
import moment from "moment";
import CustomButton from '../../../components/common/CustomButton';
import { useResourceInfo } from "../../../hooks/useResourceInfo";
import { useIntegrationStatus } from "../../../hooks/useIntegrationStatus";
import FormInput from '../../../components/common/FormInput';
import ProjectDeleteModal from '../../../components/Modals/ProjectDeleteModal';
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ENTITY, resetTimelineState, setCurrentEntity } from "../../../redux/slices/timeline";
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';
import { getEntityTypeDisplay } from "../../../utils/entityDisplayUtils";
import { capitalizeFirst } from "../../../utils/helperUtils";
import store from "../../../redux/store";
import { resetRoadmap, setSingleRoadmap } from "../../../redux/slices/roadmap";
import { smapBreadCrumbHistoryType } from "../../../utils/strategyMapUtils";

// Custom styled select component to match React Select appearance
const StyledSelect = ({ id, name, value, onChange, options, placeholder, className = '', error = false }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const selectedOption = options.find((opt: any) => opt.value === value);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                id={id}
                onClick={() => setIsOpen(!isOpen)}
                className={`block w-full min-h-[48px] px-3 py-2 text-left bg-light-surfaceSecondary dark:bg-secondary-dark border border-light-border-default dark:border-hover-dark rounded-xl shadow-sm transition-all duration-200 hover:border-light-border-interactive hover:shadow-md focus:ring-2 focus:ring-primary-light focus:border-primary-light dark:focus:ring-primary-dark dark:focus:border-primary-dark ${className}`}
            >
                <div className="flex items-center justify-between">
                    <span className={`text-base ${selectedOption ? 'text-light-text-primary dark:text-white' : 'text-light-text-muted dark:text-gray-400'}`}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                    <svg
                        className={`w-5 h-5 text-light-text-secondary dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-light-surfaceHover dark:bg-gray-700 border border-light-border-default dark:border-gray-600 rounded-lg shadow-md overflow-hidden">
                    <ul className="py-1 max-h-60 overflow-auto">
                        {options.map((option: any) => (
                            <li
                                key={option.value}
                                onClick={() => {
                                    onChange({ target: { name, value: option.value } });
                                    setIsOpen(false);
                                }}
                                className={`px-3 py-2 cursor-pointer text-base transition-colors duration-150 ${option.value === value
                                    ? 'bg-light-status-info-bg dark:bg-slate-600 text-light-text-primary dark:text-white'
                                    : 'text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-gray-600'
                                    }`}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

interface AddUpdateProjectModalProps {
    setShowModal?: any,
    isVisible?: any,
    project?: any
    onProjectUpdate?: any
}

const AddUpdateProjectModal: React.FC<AddUpdateProjectModalProps> = ({ setShowModal, isVisible = false, project = {}, onProjectUpdate }) => {

    const [searchParams] = useSearchParams();
    const breadCrumbHistory = searchParams.get("history"); 

    const JWTdata: any = useJwt()
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        createProject,
        updateProject,
        deleteProjects,
        fetchSingleProject,
        clearSingleProject
    } = useProjectService();
    const { } = useOrganizationService()
    const { fetchSingleRoadmap } = useRoadmapService();
    const { fetchAllTemplates } = useTemplateService();
    const { getResourceName } = useResourceInfo();
    const { shouldShowIntegration } = useIntegrationStatus();

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        title: false,
        description: false,
        stage: false,
        start_date: false
    });
    const [apiFieldErrors, setApiFieldErrors] = useState({
        start_date: ''
    });
    const [showStartDateErrorDetails, setShowStartDateErrorDetails] = useState(false);
    let [payloadData, setPayloadData] = useState({ ...project })
    const [orgScheduleArr, setOrgScheduleArr] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isDataSyncedRef = useRef<boolean>(false);
    const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { project_id = '' } = project
    const org_id = JWTdata.org_id;
    const user_id = JWTdata.user_id;
    const { singleProjectData, singleProjectLoading: projectLoading } = useSelector((state: any) => state.projects)
    const { templates = [] } = useSelector((state: any) => state.templates);
    const { orgSchedule = [], orgDetails } = useSelector((state: any) => state.organization);
    const { dashboardDetails } = useSelector((state: any) => state.timeline);

    // Get entity type from dashboard

 
    
    
    const getEntityType = getEntityTypeDisplay(payloadData?.entity_type, payloadData?.type);

    // Singular entity type which is converted to captial like this "Hello"
    const singularEntityType = capitalizeFirst(
      (getEntityType?.label ?? "").toLowerCase(),
    )

    const internalPriorities = useMemo(
        () => orgDetails?.priorities ?? [],
        [orgDetails?.priorities]
    );

    const internalPriorityOptions = useMemo(() => {
        return internalPriorities.map((p: any, i: any) => ({
            value: p.id,
            label: `P${i + 1}`,
            color: p.color,
        }));
    }, [internalPriorities]);

    
    const getSortedTemplates = () => {
        return [...templates].sort((a, b) =>
            a?.project_name?.toLowerCase().localeCompare(b?.project_name?.toLowerCase())
        );
    };


    const unsavedChanges = useMemo(() => {
        if (!project_id || projectLoading || !singleProjectData || Object.keys(singleProjectData).length === 0 || !isDataSyncedRef.current) {
            return false;
        }
        if (!singleProjectData.project_name) {
            return false;
        }

        const original = singleProjectData;
        const current = payloadData;

        if ((original.project_name || '') !== (current.project_name || '')) {
            return true;
        }

        const normalizeDescription = (desc: string) => {
            if (!desc) return '';
            try {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = desc;
                const textContent = tempDiv.textContent || tempDiv.innerText || '';
                let normalized = textContent.replace(/\s+/g, ' ');
                normalized = normalized.replace(/\s+([,.!?;:])/g, '$1');

                return normalized.trim();
            } catch (error) {
                console.warn('DOM-based HTML parsing failed, using regex fallback:', error);
                let normalized = desc.replace(/<[^>]*>/g, '');
                normalized = normalized.replace(/\s+/g, ' ');
                normalized = normalized.replace(/\s+([,.!?;:])/g, '$1');
                return normalized.trim();
            }
        };

        const currentDescRaw = current.project_description || '';
        const initialDescRaw = original.project_description || '';
        const isInitialLoad = !currentDescRaw && initialDescRaw;

        const currentDescNormalized = normalizeDescription(currentDescRaw);
        const initialDescNormalized = normalizeDescription(initialDescRaw);
        const descExactMatch = currentDescNormalized === initialDescNormalized;
        const descContentMatch = currentDescNormalized.replace(/\s/g, '') === initialDescNormalized.replace(/\s/g, '');

        const descriptionChanged = !isInitialLoad && !descExactMatch && !descContentMatch;
        if (descriptionChanged) {
            return true;
        }

        if ((original.stage || '') !== (current.stage || '')) {
            return true;
        }
        if ((original.priority || '') !== (current.priority || '')) {
            return true;
        }

     
        if(original.parent_info?.id !== current.parent_data?.id){
            return true;
        }

        const originalDateStr = original.start_date
            ? (original.start_date instanceof Date
                ? formatDate(original.start_date.toISOString())
                : formatDate(String(original.start_date)))
            : null;
        const currentDateStr = current.start_date
            ? (current.start_date instanceof Date
                ? formatDate(current.start_date.toISOString())
                : formatDate(String(current.start_date)))
            : null;
        if (originalDateStr !== currentDateStr) {
            return true;
        }
        if ((original.project_color || '#3b82f6') !== (current.project_color || '#3b82f6')) {
            return true;
        }

        return false;
    }, [project_id, projectLoading, singleProjectData, payloadData]);

    useEffect(() => {
        if (project?.project_id) {
            fetchSingleProject(project?.project_id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPayloadData((pv: any) => ({ ...pv, start_date: null }))
        
        
        fetchAllTemplates()
        return () => {
            clearSingleProject()
            setPayloadData({})
        }
    }, [])

    useEffect(() => {
        if (Object.keys(singleProjectData).length > 0 && !projectLoading) {
            if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
            isDataSyncedRef.current = false;
            setPayloadData(singleProjectData)
            syncTimeoutRef.current = setTimeout(() => {
                isDataSyncedRef.current = true;
            }, 250);
        } else if (Object.keys(singleProjectData).length === 0) {
            isDataSyncedRef.current = false;
        }
        return () => {
            if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        };
    }, [singleProjectData, projectLoading])

    useEffect(() => {
        // Collapse details when a new error message arrives or clears
        setShowStartDateErrorDetails(false)
    }, [apiFieldErrors.start_date])


    useEffect(() => {
        const orgScheduleArr: any = []
        if (orgSchedule.length) {
            orgSchedule.forEach((element: { id: any; date: any; }) => {
                orgScheduleArr.push(formatDate(element.date))
            });
            setOrgScheduleArr(orgScheduleArr)
        }
    }, [orgSchedule])



    const handleOnChange = (e: any) => {
        const { name, value } = e.target;
        const data = { ...payloadData, [name]: value }
        setPayloadData(data)

        // Clear validation errors when fields are filled
        if (name === 'project_name' && value) {
            setValidationErrors(prev => ({ ...prev, title: false }));
        } else if (name === 'project_description' && value) {
            const strippedContent = value?.replace(/<[^>]*>/g, '').trim();
            if (strippedContent) {
                setValidationErrors(prev => ({ ...prev, description: false }));
            }
        } else if (name === 'stage' && value) {
            setValidationErrors(prev => ({ ...prev, stage: false }));
        }
    }
    const handleOnDateChange = (date: any) => {
        const data = { ...payloadData, 'start_date': date }
        setPayloadData(data)
        if (date) {
            setValidationErrors(prev => ({ ...prev, start_date: false }));
            setApiFieldErrors(prev => ({ ...prev, start_date: '' }));
        }
    }

    const onSubmit = async () => {
        // Reset validation errors
        setValidationErrors({
            title: false,
            description: false,
            stage: false,
            start_date: false
        });

        if (!payloadData.project_name) {
            setValidationErrors(prev => ({ ...prev, title: true }));
            toast.error(`Please fill ${singularEntityType} name.`);
            return
        }
        if (!payloadData.stage) {
            setValidationErrors(prev => ({ ...prev, stage: true }));
            toast.error("Please select stage.");
            return
        }
        if (!payloadData.start_date) {
            setValidationErrors(prev => ({ ...prev, start_date: true }));
            toast.error("Please select start date.");
            return
        }

        const strippedDescription = payloadData?.project_description?.replace(/<[^>]*>/g, '').trim();
        if (!payloadData?.project_description || !strippedDescription || payloadData?.project_description === '<p></p>' || payloadData?.project_description === '<p><br></p>' || payloadData?.project_description === '<p><br/></p>') {
            setValidationErrors(prev => ({ ...prev, description: true }));
            toast.error("Description can not be null/empty !", { duration: 1000 });
            return
        }

        if (payloadData?.project_description?.length < 15) {
            toast.error("Please add min 15 Char description.");
            return
        }
        const createProjectPayload = {
            org_id,
            user_id,
            ...payloadData
        };
        createProjectPayload[`project_description`] = payloadData.project_description;
        if (createProjectPayload?.start_date) {
            createProjectPayload['start_date'] = formatDate(createProjectPayload?.start_date)
        }
        createProjectPayload['project_color'] = payloadData?.project_color || '#3b82f6';
        if (project_id) {
            setIsSubmitting(true);
            const members = payloadData.members || [];
            const UpdateProjectObject: any = {
                project_name: payloadData.project_name,
                stage: payloadData.stage,
                project_description: payloadData.project_description,
                project_color: payloadData.project_color,
                priority: payloadData.priority,
                members,
                org_id,
                parent_info: payloadData.parent_data?.id  ?? payloadData.parent_info?.id
            };
            if (payloadData?.start_date) {
                UpdateProjectObject['start_date'] = formatDate(payloadData?.start_date)
            }
            dispatch(setResourcesbyIds({ [project_id]: [] }));
            try {
                setApiFieldErrors(prev => ({ ...prev, start_date: '' }));
                const res = await updateProject(project_id, { ...UpdateProjectObject, suppressErrorToast: true, throwOnError: true });
                if (res) {
                    handleProjectUpdate(); // Close only on success
                }
            } catch (error: any) {
                const message = error?.response?.data?.detail || error?.response?.data?.error || error?.message || 'Update failed.';
                setValidationErrors(prev => ({ ...prev, start_date: true }));
                setApiFieldErrors(prev => ({ ...prev, start_date: message }));
            } finally {
                setIsSubmitting(false);
            }
        } else {
            try {
                setIsSubmitting(true);
                await createProject(createProjectPayload);
                setShowModal(false);
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleDelete = (status: boolean) => {
        setShowDeleteConfirm(status)
    }

    const submitDelete = async () => {
        if (project_id) {
            setIsDeleting(true);
            try {
                await deleteProjects(project_id, org_id);
                setShowDeleteConfirm(false);
                setShowModal(false);

                const isRoadmapPage = location.pathname.includes('/roadmaps/') || (location.pathname.split('/').filter(Boolean)[0] === 'roadmaps' && location.pathname.split('/').filter(Boolean).length > 1);
                const isProjectPage = location.pathname.includes('/projects/') || (location.pathname.split('/').filter(Boolean)[0] === 'projects' && location.pathname.split('/').filter(Boolean).length > 1);

                const historyObject = JSON.parse(breadCrumbHistory || "[]");
                
                const strategyMapPages = (
                  historyObject as smapBreadCrumbHistoryType
                ).filter((item) => item.url.includes("/strategymaps"));

                const isStragyMapPage = strategyMapPages.length > 0

                const state_data = store.getState();
                const currentEntity = state_data.timeline.currentEntity;


                if (isProjectPage || isRoadmapPage) {
                  
                  
                  if (isRoadmapPage && isStragyMapPage) {
                    const lastStrategyMapPage = strategyMapPages[strategyMapPages.length - 1];
                    navigate(lastStrategyMapPage.url);
                    return;
                  }
                  if(isProjectPage && isStragyMapPage){
                    const lastStrategyMapPage = strategyMapPages[strategyMapPages.length - 1];
                    navigate(lastStrategyMapPage.url)
                    return;
                  }

                  // if(isRoadmapPage){
                  //   navigate("/workitems")
                  // }

              

                  
                  // Feature page opened from roadmap
                  if (isRoadmapPage && currentEntity === ENTITY.PROJECT) {
                    const prevEntityId =
                      state_data.roadmap.singleRoadmapRes?.id;


                   
                      
                    // store.dispatch(resetRoadmap());

                    // store.dispatch(resetTimelineState());
                    // store.dispatch(setSingleRoadmap({}));

                   
                    
                    await fetchSingleRoadmap(prevEntityId, false);

                   
                       dispatch(
                         setCurrentEntity({
                           entity: ENTITY.ROADMAP,
                           entityId: prevEntityId,
                         }),
                       );

                    window.location.hash = "";
                  }

                  if (isProjectPage) {
                    navigate("/workitems");
                  }
                  
              
                } 
                else {
                  navigate("/workitems");
                }
            } catch (error) {
                console.error('Error deleting project:', error);
                setShowDeleteConfirm(false);
            } finally {
                setIsDeleting(false);
            }
        }
    }

    const handleProjectUpdate = () => {
        // Call the onProjectUpdate callback if provided (parent handles refresh)
        if (onProjectUpdate) {
            onProjectUpdate();
        }
        handleClose();
        // Dispatch event to reset filters on the projects page
        if (window.location.pathname === "/workitems") {
            try {
                window.dispatchEvent(new CustomEvent('reset-workunits-filters', { detail: { source: 'AddUpdateProjectModal' } }));
            } catch (e) { }
            // Don't call fetchAllProjects/fetchRecentProjects here - parent handles it via onProjectUpdate
        }
    };

    const handleClose = () => {
        clearSingleProject()
        setPayloadData({})
        setShowModal(false)
    }


    const isWeekday = (date: any, org_id: string) => {
        const day = date.getDay();
        const day_date: any = moment(date).format("YYYY-MM-DD");
        if (org_id && localStorage) {
            let workingDay: any = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : ""
            let workingDays: any = workingDay ? JSON.parse(workingDay) : {};
            let orgWorkingDays: any = workingDays[org_id] ? workingDays[org_id] : [];
            if (orgScheduleArr.includes(day_date)) {
                return false
            } else if (orgWorkingDays.length) {
                const d = day === 0 ? 7 : day
                return orgWorkingDays.includes(d);
            } else {
                return day !== 0 && day !== 6;
            }
        } else {
            if (orgScheduleArr.includes(day_date)) {
                return false
            } else {
                return day !== 0 && day !== 6;
            }
        }

    };


    const default_selected_data =
      getEntityType.type == "feature" ? { roadmap_ids: [payloadData?.parent_info?.id] } : [];


    return (
      <>
        <Drawer
          isOpen={isVisible}
          onClose={() => setShowModal(false)}
          position="right"
        >
          <div className="h-screen w-full flex flex-col bg-light-surface dark:bg-tertiary-dark overflow-hidden">
            <div className="flex-shrink-0 flex flex-row border-b z-50 rounded-b-2xl border-gray-400 dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark px-6 py-4 text-light-text-primary dark:text-white shadow-lg">
              <div className="flex w-full items-center min-h-[60px]">
                <div className="flex flex-col flex-1">
                  {projectLoading ? (
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-1"></div>
                      {project_id && (
                        <>
                          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2"></div>
                          {/* Integration data skeleton */}
                          <div className="flex flex-wrap gap-2 mt-1">
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="text-light-text-primary dark:text-white text-lg font-semibold capitalize">
                        {`${project_id ? "Update " : "New "}`}
                        {singularEntityType}
                      </div>
                      {project_id && (
                        <div className="text-light-text-secondary dark:text-white/70 text-xs mt-0.5">
                          {payloadData.created_by ? (
                            <>
                              Created by:{" "}
                              {getDefaultUserName(payloadData.created_by) ||
                                getResourceName(payloadData.created_by)}{" "}
                              on {moment(payloadData.created_at).format("ll")}
                            </>
                          ) : payloadData.created_at ? (
                            <>
                              Created on:{" "}
                              {moment(payloadData.created_at).format("ll")}
                            </>
                          ) : null}
                        </div>
                      )}

                      {/* Integration information */}
                      {!projectLoading &&
                        payloadData.integration_data &&
                        shouldShowIntegration(payloadData.integration_data) && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {payloadData.integration_data.integration_type && (
                              <Badge
                                color="indigo"
                                className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-none"
                              >
                                {payloadData.integration_data.integration_type
                                  .charAt(0)
                                  .toUpperCase() +
                                  payloadData.integration_data.integration_type
                                    .slice(1)
                                    .toLowerCase()}
                              </Badge>
                            )}
                            {payloadData.integration_data.integration_id && (
                              <Badge
                                color="info"
                                className="px-3 py-1 text-sm font-medium bg-light-status-info-bg text-light-status-info-text dark:bg-blue-900/30 dark:text-white/60 border-none"
                              >
                                <a
                                  href={
                                    payloadData.integration_data
                                      .integration_url ||
                                    `https://${payloadData.integration_data.integration_type}.com/item/${payloadData.integration_data.integration_id}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <HiLink className="mr-1" />
                                  {payloadData.integration_data.integration_id}
                                </a>
                              </Badge>
                            )}
                            {payloadData.integration_data
                              .integration_item_type && (
                              <span
                                color="purple"
                                className="px-3 py-0.5 text-xs rounded-md flex items-center justify-center font-medium bg-purple-100 text-purple-300 dark:bg-purple-900/30 !dark:text-purple-300 border-none"
                              >
                                {payloadData.integration_data.integration_item_type
                                  .charAt(0)
                                  .toUpperCase() +
                                  payloadData.integration_data.integration_item_type
                                    .slice(1)
                                    .toLowerCase()}
                              </span>
                            )}
                            {payloadData.integration_data
                              .integration_status && (
                              <Badge
                                color="success"
                                className="px-3 py-1 text-sm font-medium bg-light-status-success-bg text-light-status-success-text dark:bg-green-900/30 dark:text-green-300 border-none"
                              >
                                <span className="mr-1 font-semibold">
                                  Status:
                                </span>{" "}
                                {payloadData.integration_data.integration_status
                                  .charAt(0)
                                  .toUpperCase() +
                                  payloadData.integration_data.integration_status
                                    .slice(1)
                                    .toLowerCase()}
                              </Badge>
                            )}
                          </div>
                        )}
                    </>
                  )}
                </div>

                <div className="flex items-center gap-5">
                  {/* Delete button */}
                  {project_id && (
                    <button
                      type="button"
                      className="text-light-status-error-text hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:bg-light-status-error-bg dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                      onClick={() => handleDelete(true)}
                    >
                      <HiTrash className="text-xl" />
                    </button>
                  )}

                  <button
                    type="button"
                    className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                    onClick={() => setShowModal(false)}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-light-surface dark:bg-tertiary-dark">
              <div className="px-5 py-8 space-y-6">
                {projectLoading ? (
                  <div className="space-y-6 animate-pulse">
                    {/* Project Name and Stage Row */}
                    <div className="flex w-full gap-4">
                      <div className="space-y-2 w-full">
                        <Label
                          htmlFor="projectname"
                          className="text-base font-medium text-light-text-primary dark:text-white"
                        >
                          {singularEntityType} name*
                        </Label>
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                      </div>

                      {!project_id && (
                        <div className="space-y-2">
                          <Label
                            htmlFor="template"
                            className="text-base font-medium text-light-text-primary dark:text-white"
                          >
                            Template
                          </Label>
                          <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                        </div>
                      )}
                    </div>

                    {/* Stage and Start Date Row */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-base font-medium text-light-text-primary dark:text-white">
                          {`${singularEntityType} Stage`}*
                        </Label>
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          className="inline-flex items-center text-base font-medium text-light-text-primary dark:text-white"
                          htmlFor="start_date"
                        >
                          Start Date*
                          <Tooltip
                            placement="top"
                            content={`Default date is Today's date.`}
                          >
                            <HiQuestionMarkCircle className="ml-2 text-lg text-light-text-tertiary dark:text-gray-400" />
                          </Tooltip>
                        </Label>
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                      </div>

                      {!project_id && (
                        <div className="space-y-2 w-full">
                          <Label
                            htmlFor="template"
                            className="text-base font-medium text-light-text-primary dark:text-white"
                          >
                            Template
                          </Label>
                          <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 w-full">
                      <Label
                        htmlFor="priority"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Priority
                      </Label>
                      <CustomDropdown
                        id="priority"
                        name="priority"
                        value={payloadData.priority || ""}
                        options={internalPriorityOptions}
                        onChange={(val) =>
                          handleOnChange({
                            target: { name: "priority", value: val },
                          })
                        }
                        placeholder="Select Priority"
                        className="!mb-0"
                      />
                    </div>

                    {/* Project Color Field */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="project_color"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Select {singularEntityType} Color*
                      </Label>
                      <div className="flex items-center gap-3 px-3 py-2 border border-light-border dark:border-hover-dark rounded-xl bg-light-surfaceSecondary dark:bg-secondary-dark hover:shadow-md transition-shadow duration-200">
                        <div className="w-20 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        <div className="text-light-text-secondary dark:text-gray-400 text-sm">
                          Choose a color for your{" "}
                          {singularEntityType.toLowerCase()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex w-full gap-4">
                      <div className="space-y-2 w-full">
                        <Label
                          htmlFor="projectname"
                          className="text-base font-medium text-light-text-primary dark:text-white"
                        >
                          {singularEntityType} name*
                        </Label>
                        <FormInput
                          type="text"
                          id="projectname"
                          name="project_name"
                          value={payloadData?.project_name?.slice(0, 100) || ""}
                          onChange={handleOnChange}
                          placeholder={`${singularEntityType} name`}
                          maxLength={100}
                          className={`block w-full px-4 py-3 text-light-text-primary border border-light-border-default bg-light-surfaceSecondary dark:bg-secondary-dark text-base focus:ring-2 focus:ring-primary-light focus:border-primary-light placeholder:text-light-text-muted dark:border-hover-dark dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-xl shadow-sm hover:border-light-border-interactive hover:shadow-md transition-all duration-200 ${
                            validationErrors.title
                              ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
                              : ""
                          }`}
                          disabled={projectLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-base font-medium text-light-text-primary dark:text-white">
                          {`${singularEntityType} Stage`}*
                        </Label>
                        <StyledSelect
                          id="stage"
                          name="stage"
                          value={payloadData.stage || ""}
                          onChange={handleOnChange}
                          options={[
                            { value: "planning", label: "Planning" },
                            { value: "executing", label: "Executing" },
                            { value: "closed", label: "Closed" },
                            ...(project?.stage === "simulated"
                              ? [{ value: "simulated", label: "Simulated" }]
                              : []),
                          ]}
                          placeholder={`Choose ${singularEntityType.toLowerCase()} stage`}
                          className={
                            validationErrors.stage
                              ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
                              : ""
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        className="text-base font-medium text-light-text-primary dark:text-white"
                        htmlFor="description"
                      >
                        Description*
                      </Label>
                      <div
                        className={`rounded-xl overflow-hidden border border-light-border-default bg-light-surfaceSecondary dark:border-hover-dark shadow-sm hover:shadow-md transition-shadow duration-200 ${
                          validationErrors.description
                            ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
                            : ""
                        }`}
                      >
                        <RichTextEditor
                          content={payloadData?.project_description || ""}
                          onChange={(value) =>
                            handleOnChange({
                              target: { name: "project_description", value },
                            })
                          }
                          placeholder={`Enter ${singularEntityType.toLowerCase()} description...`}
                          disabled={projectLoading}
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <div className="space-y-2 w-full">
                        <Label
                          className="inline-flex items-center text-base font-medium text-light-text-primary dark:text-white"
                          htmlFor="start_date"
                        >
                          Start Date*
                          <Tooltip
                            placement="top"
                            content={`Default date is Today's date.`}
                          >
                            <HiQuestionMarkCircle className="ml-2 text-lg text-light-text-tertiary dark:text-gray-400" />
                          </Tooltip>
                        </Label>
                        <DatePicker
                          id="start_date"
                          className={`block w-full px-4 py-3 text-light-text-primary border border-light-border-default bg-light-surfaceSecondary dark:bg-secondary-dark text-base focus:ring-2 focus:ring-primary-light focus:border-primary-light placeholder:text-light-text-muted dark:border-hover-dark dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-xl shadow-sm hover:border-light-border-interactive hover:shadow-md transition-all duration-200 ${
                            validationErrors.start_date
                              ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
                              : ""
                          }`}
                          selected={
                            payloadData?.start_date !== null &&
                            payloadData?.start_date !== undefined &&
                            payloadData?.start_date !== "None"
                              ? new Date(payloadData?.start_date)
                              : null
                          }
                          onChange={(date: any) => handleOnDateChange(date)}
                          autoComplete="off"
                          filterDate={(date) => isWeekday(date, org_id)}
                          disabled={projectLoading}
                        />
                        {apiFieldErrors.start_date ? (
                          <div className="mt-1">
                            <div className="text-xs text-light-status-error-text dark:text-red-400 leading-snug">
                              Start date cannot be changed because a task starts
                              earlier than the selected date.
                            </div>
                            <button
                              type="button"
                              className="mt-1 text-xs text-red-600 dark:text-red-400 underline decoration-dotted hover:decoration-solid"
                              onClick={() =>
                                setShowStartDateErrorDetails(
                                  !showStartDateErrorDetails,
                                )
                              }
                            >
                              {showStartDateErrorDetails
                                ? "Hide details"
                                : "Show details"}
                            </button>
                            {showStartDateErrorDetails && (
                              <div className="mt-1 text-xs text-light-status-error-text dark:text-red-400 whitespace-pre-wrap break-words break-all">
                                {apiFieldErrors.start_date}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="space-y-2 w-full">
                      <Label
                        htmlFor="priority"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Priority
                      </Label>
                      <CustomDropdown
                        id="priority"
                        name="priority"
                        value={payloadData.priority || ""}
                        options={internalPriorityOptions}
                        onChange={(val) =>
                          handleOnChange({
                            target: { name: "priority", value: val },
                          })
                        }
                        placeholder="Select Priority"
                        className="!mb-0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="project_color"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Select {singularEntityType} Color*
                      </Label>
                      <div className="flex items-center gap-3 p-3 border border-light-border dark:border-hover-dark rounded-xl bg-light-surfaceSecondary dark:bg-secondary-dark hover:shadow-md transition-shadow duration-200">
                        <ColorPicker
                          value={payloadData?.project_color || "#3b82f6"}
                          onChange={(color) =>
                            handleOnChange({
                              target: { name: "project_color", value: color },
                            })
                          }
                          buttonClassName="w-12 h-12 rounded-lg border-2 border-light-border dark:border-hover-dark  mt-0"
                        />
                        <div className="text-light-text-secondary dark:text-gray-400 text-sm">
                          Choose a color for your{" "}
                          {singularEntityType.toLowerCase()}
                        </div>
                      </div>
                    </div>

                    {getEntityType.type == "feature" && (
                      <div className="space-y-2 w-full pb-[100px]">
                        <Label
                          htmlFor="parent_data"
                          className="text-base font-medium text-light-text-primary dark:text-white mb"
                        >
                          Select Parent
                        </Label>

                        <div className="p-4 border border-light-border dark:border-hover-dark rounded-xl bg-light-surfaceSecondary dark:bg-secondary-dark hover:shadow-md transition-shadow duration-200">
                          <div className="flex items-center gap-3 py-5 ">
                            <SelectEntities
                              defaultSelectedItems={
                                payloadData.parent_data
                                  ? [payloadData.parent_data]
                                  : []
                              }
                              defaultSelectedIds={default_selected_data as any}
                              onChange={(selected: any) => {
                                const selectedItem =
                                  selected.length > 0 ? selected[0] : null;
                                handleOnChange({
                                  target: {
                                    name: "parent_data",
                                    value: selectedItem,
                                  },
                                });
                              }}
                              searchFilterConfig={{
                                visibility: false,
                                value: "container_epic",
                              }}
                              entityTypeLabel="Entity"
                              selectType="single"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 px-8 py-4 items-center flex gap-6 border-t border-gray-500 rounded-t-md dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg">
              <CustomButton
                variant="cancel"
                onClick={handleClose}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105"
              >
                Cancel
              </CustomButton>
              <div className="ml-auto flex items-center gap-4">
                {project_id && (
                  <UnsavedChangesIndicator hasUnsavedChanges={unsavedChanges} />
                )}
                <CustomButton
                  onClick={onSubmit}
                  disabled={
                    projectLoading ||
                    isSubmitting ||
                    (project_id && !unsavedChanges)
                  }
                  isLoading={projectLoading || isSubmitting}
                  className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                >
                  {isSubmitting
                    ? project_id
                      ? "Updating..."
                      : "Creating..."
                    : project_id
                      ? "Update"
                      : "Create"}
                </CustomButton>
              </div>
            </div>
          </div>
        </Drawer>
        <ProjectDeleteModal
          showModal={showDeleteConfirm}
          setShowModal={handleDelete}
          handleConfirm={submitDelete}
          projectName={payloadData?.project_name}
          isDeleting={isDeleting}
        />
      </>
    );

}

export default AddUpdateProjectModal;